import { IPROP_MAP, modifyProps } from '../../generators/modifyProps';

import { JDSSearchResultsProps } from './generated';
import { LinkProps } from '../jdslink/logic';
import { ListBlockProps } from '../jdslistblock/logic';
import { MouseEvent } from 'react';

export interface SearchResultsRefProps {
  handleArrowDown: () => void;
  handleArrowUp: () => void;
  resetHighlighted: () => void;
}

export interface SearchResultsItemProps extends HighlightedProps {
  item: string | ListBlockProps | undefined;
}

export interface SearchResultSectionProps {
  /**
   * List of the results to be displayed in the SearchResults panel.
   * Component accepts item to be of type `string` or `ListBlock`.
   * If item is of type `string` internally it will generate `ListBlock` where title will be the string that is passed.
   */
  results?: (LinkProps & ListBlockProps)[];
  /**
   * A Heading that will display in the Header of the SearchResults
   */
  header?: string;
  /**
   * If passed a button will appear on the right side of the Heading in the Header
   */
  headerAction?: LinkProps;
  /**
   * A Heading that will display in the Footer of the SearchResults
   */
  footer?: string;
  /**
   * If passed a button will appear on the right side of the Heading in the Footer
   */
  footerAction?: LinkProps;
  /**
   * Click event for `headerAction` button. Passing this makes the button send click events.
   */
  onHeaderActionClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  /**
   * Click event for `footerAction` button. Passing this makes the button send click events.
   */
  onFooterActionClick?: (e: MouseEvent<HTMLButtonElement>) => void;
}

export interface SearchResultsProps
  extends Pick<
    JDSSearchResultsProps,
    'open' | 'query' | 'appearance' | 'custom'
  > {
  /**
   * Method is invoked if result item has been selected.
   * Item can be selected by clicking with a mouse, tapping on the phone or pressing Enter while item has been highlighted
   * @param item
   */
  onResultsItemClick?: (item: SearchResultsItemProps) => void;
  /**
   * Method is invoked if result item has been highlighted.
   * Item can be highlighted by hovering with the mouse over item, Tabbing into item or using ArrowDown/ArrowUp on the keyboard
   * @param item
   */
  onResultsItemHighlighted?: (item: SearchResultsItemProps) => void;
  results?: { icon: string; label: string }[];

  header?: string;

  headerAction?: {
    title: string;
    onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  };
}

export const PROP_MAP: IPROP_MAP<SearchResultsProps> = (props) => ({
  properties: {
    results: props.results?.map((r) => ({
      ...r,
      title: r.label,
      icon: r.icon
    })),
    header: props.header,
    headerAction: props.headerAction?.title,
    onHeaderActionClick: props.headerAction?.onClick
  }
});

export const withLogic =
  (
    Component: React.ComponentType<SearchResultsProps>
  ): React.FC<SearchResultsProps> =>
  (userProps: SearchResultsProps) => {
    const props = modifyProps(userProps, PROP_MAP);
    return (
      <>
        <Component
          {...{
            ...props,
            _onContainerClick: (ev: MouseEvent<HTMLDivElement>) =>
              ev.stopPropagation()
          }}
        />
      </>
    );
  };
